// ProfileStep.js - Student profile information step
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';

const LEARNING_STYLES = [
  { id: 'visual', label: 'Visual', icon: 'eye' },
  { id: 'auditory', label: 'Auditory', icon: 'ear-hearing' },
  { id: 'reading', label: 'Reading/Writing', icon: 'book-open-page-variant' },
  { id: 'kinesthetic', label: 'Hands-on', icon: 'hand-pointing-up' },
];

const ProfileStep = ({
  profileData = {},
  onProfileChange,
  onNext,
  onBack,
}) => {
  const { isDark } = useTheme();
  const [profile, setProfile] = useState({
    grade: profileData.grade || '',
    age: profileData.age || '',
    passion: profileData.passion || '',
    favorite_stem_subject: profileData.favorite_stem_subject || '',
    extracurricular_activity: profileData.extracurricular_activity || '',
    preferred_learning_style: profileData.preferred_learning_style || 'visual',
  });

  useEffect(() => {
    setProfile({
      grade: profileData.grade || '',
      age: profileData.age || '',
      passion: profileData.passion || '',
      favorite_stem_subject: profileData.favorite_stem_subject || '',
      extracurricular_activity: profileData.extracurricular_activity || '',
      preferred_learning_style: profileData.preferred_learning_style || 'visual',
    });
  }, [profileData]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#2563eb',
    accentLight: isDark ? '#2563eb30' : '#2563eb15',
    border: isDark ? '#475569' : '#e2e8f0',
    inputBg: isDark ? '#1e293b' : '#f8fafc',
    selectedBorder: '#2563eb',
    selectedBg: isDark ? '#2563eb25' : '#2563eb10',
  };

  const updateField = (field, value) => {
    const updated = { ...profile, [field]: value };
    setProfile(updated);
    onProfileChange?.(updated);
  };

  const isValid = profile.grade && profile.age;

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        style={[styles.container, { backgroundColor: colors.bg }]}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {/* Title */}
          <Text style={[styles.title, { color: colors.text }]}>
            Tell us about yourself
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            This helps personalize your learning experience
          </Text>

          {/* Form Fields */}
          <View style={styles.formContainer}>
            {/* Grade */}
            <View style={styles.fieldGroup}>
              <View style={styles.labelRow}>
                <MaterialCommunityIcons name="school" size={18} color={colors.accent} />
                <Text style={[styles.label, { color: colors.text }]}>Grade *</Text>
              </View>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: colors.inputBg,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                value={profile.grade}
                onChangeText={(text) => updateField('grade', text)}
                placeholder="e.g., 4, 5, 6... 10, 11, 12"
                placeholderTextColor={colors.textSecondary}
                keyboardType="numeric"
              />
            </View>

            {/* Age */}
            <View style={styles.fieldGroup}>
              <View style={styles.labelRow}>
                <MaterialCommunityIcons name="cake-variant" size={18} color={colors.accent} />
                <Text style={[styles.label, { color: colors.text }]}>Age *</Text>
              </View>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: colors.inputBg,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                value={profile.age}
                onChangeText={(text) => updateField('age', text)}
                placeholder="e.g., 16"
                placeholderTextColor={colors.textSecondary}
                keyboardType="numeric"
              />
            </View>

            {/* Passion */}
            <View style={styles.fieldGroup}>
              <View style={styles.labelRow}>
                <MaterialCommunityIcons name="heart" size={18} color="#ef4444" />
                <Text style={[styles.label, { color: colors.text }]}>Passion / Hobby</Text>
              </View>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: colors.inputBg,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                value={profile.passion}
                onChangeText={(text) => updateField('passion', text)}
                placeholder="e.g., Soccer, Music, Art"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {/* Favorite STEM Subject */}
            <View style={styles.fieldGroup}>
              <View style={styles.labelRow}>
                <MaterialCommunityIcons name="flask" size={18} color="#10b981" />
                <Text style={[styles.label, { color: colors.text }]}>Favorite STEM Subject</Text>
              </View>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: colors.inputBg,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                value={profile.favorite_stem_subject}
                onChangeText={(text) => updateField('favorite_stem_subject', text)}
                placeholder="e.g., Physics, Biology, Math"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {/* Extracurricular Activity */}
            <View style={styles.fieldGroup}>
              <View style={styles.labelRow}>
                <MaterialCommunityIcons name="run" size={18} color="#8b5cf6" />
                <Text style={[styles.label, { color: colors.text }]}>Extracurricular Activity</Text>
              </View>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: colors.inputBg,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                value={profile.extracurricular_activity}
                onChangeText={(text) => updateField('extracurricular_activity', text)}
                placeholder="e.g., Robotics Club, Debate Team"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {/* Learning Style */}
            <View style={styles.fieldGroup}>
              <View style={styles.labelRow}>
                <MaterialCommunityIcons name="brain" size={18} color="#f59e0b" />
                <Text style={[styles.label, { color: colors.text }]}>Learning Style</Text>
              </View>
              <View style={styles.learningStyleGrid}>
                {LEARNING_STYLES.map((style) => {
                  const isSelected = profile.preferred_learning_style === style.id;
                  return (
                    <TouchableOpacity
                      key={style.id}
                      style={[
                        styles.styleOption,
                        {
                          backgroundColor: isSelected ? colors.selectedBg : colors.inputBg,
                          borderColor: isSelected ? colors.selectedBorder : colors.border,
                        },
                      ]}
                      onPress={() => updateField('preferred_learning_style', style.id)}
                      activeOpacity={0.7}
                    >
                      <MaterialCommunityIcons
                        name={style.icon}
                        size={20}
                        color={isSelected ? colors.accent : colors.textSecondary}
                      />
                      <Text
                        style={[
                          styles.styleLabel,
                          { color: isSelected ? colors.accent : colors.text },
                        ]}
                      >
                        {style.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>

          {/* Navigation Buttons */}
          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={[styles.secondaryButton, { borderColor: colors.border }]}
              onPress={onBack}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
              <Text style={[styles.secondaryButtonText, { color: colors.text }]}>Back</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.primaryButton,
                { backgroundColor: isValid ? colors.accent : colors.border },
              ]}
              onPress={onNext}
              activeOpacity={0.8}
              disabled={!isValid}
            >
              <Text style={[styles.primaryButtonText, { opacity: isValid ? 1 : 0.5 }]}>
                Continue
              </Text>
              <MaterialCommunityIcons
                name="arrow-right"
                size={18}
                color="#fff"
                style={{ opacity: isValid ? 1 : 0.5 }}
              />
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 28,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  formContainer: {
    gap: 18,
    marginBottom: 24,
  },
  fieldGroup: {
    gap: 8,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
  },
  learningStyleGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  styleOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1.5,
    gap: 6,
  },
  styleLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default ProfileStep;
